
import {
	AddDataSharedFunc, DashboardSharedFilterFunc, DashboardSharedFilterKey,
	IDashboardSharedChartData, IDashboardSharedDimensionsViewModel,
} from "models/common";
import { IFilters, IFilterKeys, DimensionsById, ISourceData, IDimFacility, IDimension, IDimMDT } from "models/core";
import { IDashboardViewModel } from "./viewModel";

//Plots for this dashboard
import * as PrimarySite from "./primarySiteHierarchy";
import * as Morphology from "./morphology";
import * as Remoteness from "../../../common/plotdata/remoteness";
import * as HHSOfResidence from "../../../common/plotdata/hhsOfResidence";

export type { IDashboardViewModel } from "./viewModel";

export type IDashboardSource = {
	overall: IDashboardViewModel[];
	hypopharynx: IDashboardViewModel[];
	nasalCavityAndParanasalSinuses: IDashboardViewModel[];
	majorSalivaryGlands: IDashboardViewModel[];
	oropharynx: IDashboardViewModel[];
	otherPharynx: IDashboardViewModel[];
	oralCavity: IDashboardViewModel[];
	larynx: IDashboardViewModel[];
	nasopharynx: IDashboardViewModel[];
}

export interface IDashboardDimensionsViewModel extends IDashboardSharedDimensionsViewModel {
    hhsResidenceGroups?: DimensionsById;
    dataQualityIndicators?: DimensionsById;
    monthsArray?: Array<IDimension>;
    yearsArray?: Array<IDimension>;
    months?: { [monthId: number]: string };
    years?: { [yearId: number]: string };
    dqCompFacility?: { [facilityYearKey: string]: number };
    dqCompState?: { [facilityYearKey: string]: number };
    oneYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    threeYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    fiveYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    tenYearSurvivalMDT?: { [mdtDiagYearKey: string]: { "MDT": number, "State": number } };
    kmSurvivalMDT?: {[mdtCancerKey: string]: { survivalTime: string, mdtKMSurvival: number, stateKMSurvival: number }};

}

interface ILevel1ChartData {
	totalDiagnoses: number;
	totalDataQualityEligibleDiagnoses: number;
	facilities: IDimFacility[];
	hhses: IDimension[];
	mdts: IDimMDT[];
	month: IDimension[];
	year: IDimension[];

    hadSurgery: { numerator: number, denominator: number, percentage: number };
	diagnosisToSurgeryLT30Days: { numerator, denominator: number, percentage: number };
	lengthOfStay: { min: number, median: number, max: number; };
	mortality90Days: { numerator: number, denominator: number, percentage: number; };
	twoYearSurvival: { numerator: number, denominator: number, percentage: number; };
	hadIVST: { numerator: number, denominator: number, percentage: number };
	diagnosisToIVSTLT45Days: { numerator: number, denominator: number, percentage: number; };
	diagnosisToConcurrentCRTLT45Days: { numerator: number, denominator: number, percentage: number };
	hadRT: { numerator: number, denominator: number, percentage: number };
	diagnosisToRTLT45Days: { numerator: number, denominator: number, percentage: number };
	// Export
	filteredReferralIdCsv: string;
}

// HACK: chop out the reference array from the shared interface 
type RestrictedSharedChartData = Omit<Omit<IDashboardSharedChartData, "facilities">, "hhses">;

interface ILevel2ChartData extends RestrictedSharedChartData {
	totalDiagnoses: number;
	primarySite: PrimarySite.IHierarchyPlotData;
	morphology: Morphology.IPlotData;
	remoteness: Remoteness.IPlotData;
	hhsOfResidence: HHSOfResidence.IPlotData;
}

export interface IChartData {
	hypopharynx: {
		level1: ILevel1ChartData;
		level2: ILevel2ChartData;
	},
	nasopharynx: { 
		level1: ILevel1ChartData;
		level2: ILevel2ChartData;
	}
	larynx: { 
		level1: ILevel1ChartData;
		level2: ILevel2ChartData;
	},
	majorSalivaryGlands: { 
		level1: ILevel1ChartData;
		level2: ILevel2ChartData;
	},
	nasalCavityAndParanasalSinuses: { 
		level1: ILevel1ChartData;
		level2: ILevel2ChartData;
	},
	oralCavity: { 
		level1: ILevel1ChartData;
		level2: ILevel2ChartData;
	},
	oropharynx: { 
		level1: ILevel1ChartData;
		level2: ILevel2ChartData;
	},
	overall: { 
		level1: ILevel1ChartData;
		level2: ILevel2ChartData;
	},
	otherPharynx: { 
		level1: ILevel1ChartData;
		level2: ILevel2ChartData;
	}
	

}

const BreastFilterKeys: IFilterKeys = {
	//Filter keys for breast indicators
	DiagnosisYear: { key: "DiagnosisYear", label: "Diagnosis Year" },
	DiagnosisMonth: { key: "DiagnosisMonth", label: "Diagnosis Month" },
	MdtYear: { key: "MdtYear", label: "MDT Year" },
	MdtMonth: { key: "MdtMonth", label: "MDT Month" },
	Invasive: { key: "Invasive", label: "Invasive / In-situ" },
	PrimarySite: { key: "PrimarySite", label: "Primary site" },
	Morphology: { key: "Morphology", label: "Morphology" },
	Remoteness: { key: "Remoteness", label: "Rurality" },
	DefinitiveMastectomy: { key: "DefinitiveMastectomy", label: "Definitive Mastectomy" },
	HHSOfResidence: { key: "HHSOfResidence", label: "HHS of residence" }
}

export const DashboardFilterKeys: IFilterKeys = {
	...DashboardSharedFilterKey,
	...BreastFilterKeys
}

export type Filters = IFilters<typeof DashboardFilterKeys>;
export type DashboardFilterFunc = DashboardSharedFilterFunc<IDashboardViewModel>;
export type AddDataFunc = AddDataSharedFunc<IDashboardViewModel, ISourceData>;